import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IItemCustomization } from "@faire/web-api/indigofair/data/IItemCustomization";
import { IProductCustomization } from "@faire/web-api/indigofair/data/IProductCustomization";
import { logError } from "@faire/web/common/logging";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { ObservableMap, ObservableSet } from "mobx";

export const isProductCustomizationActive = (
  customization: Pick<IProductCustomization, "active">
) => !!customization.active;

export const hasActiveProductCustomizations = (
  customizations: Pick<IProductCustomization, "active">[]
) => customizations.some(isProductCustomizationActive);

export const isProductCustomizationRequired = (
  customization: Pick<IProductCustomization, "required">
) => !!customization.required;

const getProductCustomizationsAdditionalCharge = (
  customizations: IProductCustomization[]
): IFaireMoney | undefined => {
  if (customizations.length === 0) {
    return undefined;
  }
  return MoneyMath.sum(
    ...customizations
      .filter(isProductCustomizationActive)
      .map(
        (customization) =>
          customization.additional_promo_charge?.discounted_item_price ??
          customization.additional_charge
      )
  );
};

export const getAppliedProductCustomizationsAdditionalCharge = (
  customizations: IProductCustomization[],
  appliedCustomizations: Map<string, string>
) =>
  getProductCustomizationsAdditionalCharge(
    customizations.filter((c) =>
      isProductCustomizationApplied(c, appliedCustomizations)
    )
  );

export const getRequiredProductCustomizationsAdditionalCharge = (
  customizations: IProductCustomization[]
) =>
  getProductCustomizationsAdditionalCharge(
    customizations.filter(isProductCustomizationRequired)
  );

export const getProductCustomizationMinimumOrderQuantity = (
  customization: Pick<IProductCustomization, "min_order_quantity">
) =>
  typeof customization.min_order_quantity === "number"
    ? customization.min_order_quantity
    : -Infinity;

export const getProductCustomizationsMinimumOrderQuantity = (
  customizations: Pick<IProductCustomization, "min_order_quantity" | "active">[]
) =>
  Math.max(
    ...customizations
      .filter(isProductCustomizationActive)
      .map(getProductCustomizationMinimumOrderQuantity)
  );

export const isProductCustomizationAppliedFromItemCustomizations = (
  customization: IProductCustomization,
  itemCustomizations: IItemCustomization[]
) => {
  if (!isProductCustomizationActive(customization)) {
    return false;
  }

  return itemCustomizations.some(
    (itemCustomization) =>
      itemCustomization.product_customization_token === customization.token
  );
};

export const isProductCustomizationAppliedFromMap = (
  customization: IProductCustomization,
  appliedCustomizations: Map<string, string>
) => {
  if (!isProductCustomizationActive(customization)) {
    return false;
  }

  if (!customization.token) {
    return false;
  }

  const applied = appliedCustomizations.get(customization.token);

  return applied !== undefined && applied.trim().length > 0;
};

export const isProductCustomizationAppliedFromSet = (
  customization: IProductCustomization,
  appliedCustomizations: Set<string>
) =>
  isProductCustomizationActive(customization) &&
  !!customization.token &&
  appliedCustomizations.has(customization.token);

const isProductCustomizationApplied = (
  customization: IProductCustomization,
  appliedCustomizations:
    | Map<string, string>
    | IItemCustomization[]
    | Set<string>
) => {
  if (Array.isArray(appliedCustomizations)) {
    return isProductCustomizationAppliedFromItemCustomizations(
      customization,
      appliedCustomizations
    );
  } else if (
    appliedCustomizations instanceof Map ||
    appliedCustomizations instanceof ObservableMap
  ) {
    return isProductCustomizationAppliedFromMap(
      customization,
      appliedCustomizations
    );
  } else if (
    appliedCustomizations instanceof ObservableSet ||
    appliedCustomizations instanceof Set
  ) {
    return isProductCustomizationAppliedFromSet(
      customization,
      appliedCustomizations
    );
  }
  logError(
    new Error(
      `Unexpected value ${appliedCustomizations} for appliedCustomizations in isProductCustomizationApplied`
    )
  );
  return false;
};

export const getAppliedProductCustomizations = (
  customizations: IProductCustomization[],
  appliedCustomizations:
    | Map<string, string>
    | IItemCustomization[]
    | Set<string>
) =>
  customizations.filter((c) =>
    isProductCustomizationApplied(c, appliedCustomizations)
  );

export const areProductCustomizationsApplied = (
  customizations: IProductCustomization[],
  appliedCustomizations: Map<string, string>
) =>
  customizations.every((c) =>
    isProductCustomizationApplied(c, appliedCustomizations)
  );

export const areRequiredProductCustomizationsApplied = (
  customizations: IProductCustomization[],
  appliedCustomizations: Map<string, string>
) =>
  areProductCustomizationsApplied(
    customizations.filter(isProductCustomizationRequired),
    appliedCustomizations
  );
